import { useState } from "react";
import { Plan } from "../../../models/SubscriptionModels";
import {
  CreatePlanRequest,
  KNOWN_FEATURE_KEYS,
  PlanEditorValues,
  PlanFeatureInput,
  fromPlanToEditorValues,
  toCreatePlanRequest,
} from "../../../models/PlanModels";
import "./PlanForm.css";

interface PlanFormProps {
  initialPlan: Plan | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (request: CreatePlanRequest, planId?: string) => void;
}

const createEmptyValues = (): PlanEditorValues => ({
  code: "",
  name: "",
  description: "",
  priceMonthly: 0,
  currency: "BDT",
  billingPeriodDays: 30,
  isActive: true,
  isPublic: true,
  sortOrder: 0,
  features: [],
});

const PlanForm = ({
  initialPlan,
  submitting,
  onCancel,
  onSubmit,
}: PlanFormProps) => {
  const [form, setForm] = useState<PlanEditorValues>(
    initialPlan ? fromPlanToEditorValues(initialPlan) : createEmptyValues(),
  );

  const setField = <K extends keyof PlanEditorValues>(
    key: K,
    value: PlanEditorValues[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const setFeature = (index: number, patch: Partial<PlanFeatureInput>) => {
    setForm((prev) => {
      const next = prev.features.map((f, i) =>
        i === index ? { ...f, ...patch } : f,
      );
      return { ...prev, features: next };
    });
  };

  const addFeature = () =>
    setForm((prev) => ({
      ...prev,
      features: [...prev.features, { featureKey: "", featureValue: "" }],
    }));

  const removeFeature = (index: number) =>
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(toCreatePlanRequest(form), initialPlan?.id);
  };

  const hasPartialFeature = form.features.some(
    (f) =>
      (!!f.featureKey.trim() && !f.featureValue.trim()) ||
      (!f.featureKey.trim() && !!f.featureValue.trim()),
  );

  return (
    <form className="plan-form" onSubmit={handleSubmit}>
      <div className="plan-form__grid">
        <div className="plan-form__field">
          <label htmlFor="plan-code">Code *</label>
          <input
            id="plan-code"
            type="text"
            value={form.code}
            placeholder="e.g. starter"
            disabled={submitting}
            onChange={(e) => setField("code", e.target.value)}
          />
          <span className="plan-form__hint">
            Unique identifier shown on subscription records.
          </span>
        </div>

        <div className="plan-form__field">
          <label htmlFor="plan-name">Name *</label>
          <input
            id="plan-name"
            type="text"
            value={form.name}
            placeholder="e.g. Starter Plan"
            disabled={submitting}
            onChange={(e) => setField("name", e.target.value)}
          />
        </div>

        <div className="plan-form__field plan-form__field--full">
          <label htmlFor="plan-description">Description</label>
          <textarea
            id="plan-description"
            rows={2}
            value={form.description}
            disabled={submitting}
            onChange={(e) => setField("description", e.target.value)}
          />
        </div>

        <div className="plan-form__field">
          <label htmlFor="plan-price">Price (monthly) *</label>
          <input
            id="plan-price"
            type="number"
            min={0}
            step="0.01"
            value={form.priceMonthly}
            disabled={submitting}
            onChange={(e) =>
              setField("priceMonthly", Number(e.target.value))
            }
          />
        </div>

        <div className="plan-form__field">
          <label htmlFor="plan-currency">Currency</label>
          <input
            id="plan-currency"
            type="text"
            value={form.currency}
            disabled={submitting}
            onChange={(e) => setField("currency", e.target.value)}
          />
        </div>

        <div className="plan-form__field">
          <label htmlFor="plan-period">Billing period (days) *</label>
          <input
            id="plan-period"
            type="number"
            min={1}
            value={form.billingPeriodDays}
            disabled={submitting}
            onChange={(e) =>
              setField("billingPeriodDays", Number(e.target.value))
            }
          />
        </div>

        <div className="plan-form__field">
          <label htmlFor="plan-sort">Sort order</label>
          <input
            id="plan-sort"
            type="number"
            value={form.sortOrder}
            disabled={submitting}
            onChange={(e) => setField("sortOrder", Number(e.target.value))}
          />
        </div>

        <div className="plan-form__field">
          <label className="plan-form__check">
            <input
              type="checkbox"
              checked={form.isActive}
              disabled={submitting}
              onChange={(e) => setField("isActive", e.target.checked)}
            />
            Active
          </label>
          <label className="plan-form__check">
            <input
              type="checkbox"
              checked={form.isPublic}
              disabled={submitting}
              onChange={(e) => setField("isPublic", e.target.checked)}
            />
            Public (visible to customers)
          </label>
        </div>
      </div>

      <div className="plan-form__section">
        <div className="plan-form__section-head">
          <h4>Features</h4>
          <span className="plan-form__hint">
            Saving replaces the full feature list.
          </span>
        </div>

        <datalist id="plan-feature-keys">
          {KNOWN_FEATURE_KEYS.map((key) => (
            <option key={key} value={key} />
          ))}
        </datalist>

        {form.features.length === 0 && (
          <p className="plan-form__empty">No features defined yet.</p>
        )}

        {hasPartialFeature && (
          <p className="plan-form__warn">
            Each feature row needs both a key and a value — rows with only one
            filled will be ignored.
          </p>
        )}

        <div className="plan-form__features">
          {form.features.map((feature, index) => (
            <div className="plan-form__feature-row" key={index}>
              <input
                type="text"
                list="plan-feature-keys"
                placeholder="feature key (e.g. max_branches)"
                aria-label={`Feature key ${index + 1}`}
                value={feature.featureKey}
                disabled={submitting}
                onChange={(e) =>
                  setFeature(index, { featureKey: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="value (e.g. 2)"
                aria-label={`Feature value ${index + 1}`}
                value={feature.featureValue}
                disabled={submitting}
                onChange={(e) =>
                  setFeature(index, { featureValue: e.target.value })
                }
              />
              <button
                type="button"
                className="plan-form__remove"
                disabled={submitting}
                aria-label={`Remove feature ${index + 1}`}
                onClick={() => removeFeature(index)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="plan-form__add"
          disabled={submitting}
          onClick={addFeature}
        >
          + Add feature
        </button>
      </div>

      <div className="plan-form__actions">
        <button
          type="button"
          className="plan-form__btn plan-form__btn--secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="plan-form__btn plan-form__btn--primary"
          disabled={
            submitting ||
            !form.code.trim() ||
            !form.name.trim() ||
            form.billingPeriodDays < 1 ||
            form.priceMonthly < 0 ||
            hasPartialFeature
          }
        >
          {submitting
            ? "Saving…"
            : initialPlan
              ? "Save changes"
              : "Create plan"}
        </button>
      </div>
    </form>
  );
};

export default PlanForm;